import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, db, storage } from './firebase';
import { sanitizeObject, sanitizeArray, validateAndSanitizeText } from './utils/sanitization';

// Get environment for Firestore collections
const getEnvironment = () => {
  return process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
};

const COLLECTION_NAME = `agriplanner/${getEnvironment()}/userSettings`;

// Status constants for saving and loading
const SAVE_STATUS = {
  IDLE: 'idle',
  SAVING: 'saving',
  ERROR: 'error',
  RETRYING: 'retrying'
};

const LOAD_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  ERROR: 'error'
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE);
  const [loadStatus, setLoadStatus] = useState(LOAD_STATUS.IDLE);
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [emailLinkSignInRequired, setEmailLinkSignInRequired] = useState(false);

  const [mapState, setMapState] = useState({
    center: { lat: 59.3293, lng: 18.0686 },
    zoom: 10,
    mapTypeId: 'roadmap'
  });
  const [borderData, setBorderData] = useState([]);
  const [gamefenceData, setGamefenceData] = useState([]);
  const [poleDistance, setPoleDistance] = useState(3);
  const [gamefenceClosed, setGamefenceClosed] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [todoEntries, setTodoEntries] = useState([]);
  const [packlists, setPacklists] = useState([]);
  const [decisionEntries, setDecisionEntries] = useState([]);
  const [images, setImages] = useState([]);
  const [cropAreas, setCropAreas] = useState([]);
  const [cropAreaWidth, setCropAreaWidth] = useState(10);
  const [separationWidth, setSeparationWidth] = useState(0.5);
  const [separationWidthLongitudinal, setSeparationWidthLongitudinal] = useState(0.5);
  const [gpsPoints, setGpsPoints] = useState([]);
  const [plantGroups, setPlantGroups] = useState([]);

  // Track retry attempts
  const retryTimeouts = useRef(new Map());
  const retryStartTimes = useRef(new Map());

  const loginWithEmail = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const signUpWithEmail = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // Firebase configuration required: enable "Email link (passwordless sign-in)"
  // under Authentication > Sign-in methods in the Firebase Console.
  // The redirect URL (window.location.origin) must be listed under
  // Authentication > Settings > Authorized domains (localhost is included by default).
  const sendLoginLink = async (email) => {
    const actionCodeSettings = {
      url: window.location.origin,
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    localStorage.setItem('emailForSignIn', email);
  };

  const completeSignInWithLink = async (email) => {
    const result = await signInWithEmailLink(auth, email, window.location.href);
    localStorage.removeItem('emailForSignIn');
    setEmailLinkSignInRequired(false);
    window.history.replaceState({}, document.title, window.location.pathname);
    return result.user;
  };

  // Detect and complete email link sign-in on page load
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;

    const storedEmail = localStorage.getItem('emailForSignIn');
    if (storedEmail) {
      signInWithEmailLink(auth, storedEmail, window.location.href)
        .then(() => {
          localStorage.removeItem('emailForSignIn');
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch((error) => {
          console.error('Error completing email link sign-in:', error);
          setEmailLinkSignInRequired(true);
        });
    } else {
      // Link opened in a different browser — user must confirm email
      setEmailLinkSignInRequired(true);
    }
  }, []);

  /**
   * Generic function to save data to Firestore with retry logic
   * @param {string} field - The field name to save
   * @param {any} data - The data to save
   * @param {number} attempt - Current retry attempt number
   */
  const saveToFirestore = async (field, data, attempt = 0) => {
    if (!user) {
      console.warn('No user logged in, skipping Firestore save');
      return;
    }

    const maxRetryDuration = 60000; // 60 seconds
    const retryDelay = Math.min(1000 * Math.pow(2, attempt), 10000); // Exponential backoff, max 10s

    try {
      setSaveStatus(SAVE_STATUS.SAVING);

      await setDoc(doc(db, COLLECTION_NAME, user.uid), {
        [field]: data,
        updatedAt: new Date()
      }, { merge: true });

      // Clear any existing retry for this field
      if (retryTimeouts.current.has(field)) {
        clearTimeout(retryTimeouts.current.get(field));
        retryTimeouts.current.delete(field);
        retryStartTimes.current.delete(field);
      }

      setSaveStatus(SAVE_STATUS.IDLE);
      setSaveError(null);
    } catch (error) {
      console.error(`Error saving ${field} to Firestore (attempt ${attempt + 1}):`, error);

      // Check if we should retry
      const startTime = retryStartTimes.current.get(field) || Date.now();
      retryStartTimes.current.set(field, startTime);
      const elapsedTime = Date.now() - startTime;

      if (elapsedTime < maxRetryDuration) {
        // Schedule retry
        setSaveStatus(SAVE_STATUS.RETRYING);
        setSaveError(`Save failed, retrying... (${Math.ceil((maxRetryDuration - elapsedTime) / 1000)}s remaining)`);

        const timeout = setTimeout(() => {
          saveToFirestore(field, data, attempt + 1);
        }, retryDelay);

        retryTimeouts.current.set(field, timeout);
      } else {
        // Max retry duration exceeded
        setSaveStatus(SAVE_STATUS.ERROR);
        setSaveError(`Failed to save ${field} after 60 seconds. Please check your connection.`);
        retryStartTimes.current.delete(field);
      }
    }
  };

  /**
   * Generic function to load data from Firestore
   * @param {string} field - The field name to load
   * @param {Function} setter - The state setter function
   * @param {any} defaultValue - Default value if no data exists
   * @param {Object} userObj - The user object to use (passed from onAuthStateChanged)
   */
  const loadFromFirestore = async (field, setter, defaultValue, userObj = user) => {
    if (!userObj) {
      // Set default value when not logged in
      setter(defaultValue);
      return;
    }

    try {
      setLoadStatus(LOAD_STATUS.LOADING);

      const docRef = doc(db, COLLECTION_NAME, userObj.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const firestoreData = docSnap.data()[field];
        if (firestoreData !== undefined) {
          setter(firestoreData);
          setLoadStatus(LOAD_STATUS.IDLE);
          setLoadError(null);
          return firestoreData;
        }
      }

      // Set default if no data exists
      setter(defaultValue);
      setLoadStatus(LOAD_STATUS.IDLE);
      setLoadError(null);
    } catch (error) {
      console.error(`Error loading ${field} from Firestore:`, error);
      setter(defaultValue);
      setLoadStatus(LOAD_STATUS.ERROR);
      setLoadError(`Failed to load ${field}. Using default values.`);
    }

    return null;
  };

  const saveMapState = async (center, zoom, mapTypeId) => {
    const newMapState = { center, zoom, mapTypeId: mapTypeId || mapState.mapTypeId };
    setMapState(newMapState);
    await saveToFirestore('mapState', newMapState);
  };

  const saveBorderData = async (points) => {
    setBorderData(points);
    await saveToFirestore('borderData', points);
  };

  const saveGamefenceData = async (points) => {
    setGamefenceData(points);
    await saveToFirestore('gamefenceData', points);
  };

  const savePoleDistance = async (distance) => {
    setPoleDistance(distance);
    await saveToFirestore('poleDistance', distance);
  };

  const saveGamefenceClosed = async (closed) => {
    setGamefenceClosed(closed);
    await saveToFirestore('gamefenceClosed', closed);
  };

  const saveJournalEntry = async (entry) => {
    // Sanitize all text fields in the journal entry
    const sanitizedEntry = sanitizeObject(entry, ['text', 'labels'], 5000);

    const updatedEntries = [...journalEntries, sanitizedEntry];
    setJournalEntries(updatedEntries);
    await saveToFirestore('journalEntries', updatedEntries);
  };

  const saveTodoEntry = async (entry) => {
    // Sanitize text field in the todo entry
    const sanitizedEntry = sanitizeObject(entry, ['text', 'plantGroupName'], 2000);

    const updatedEntries = [...todoEntries, sanitizedEntry];
    setTodoEntries(updatedEntries);
    await saveToFirestore('todoEntries', updatedEntries);
  };

  const markTodoCompleted = async (todoId) => {
    const updatedEntries = todoEntries.map(todo => {
      if (todo.id === todoId) {
        return {
          ...todo,
          completed: !todo.completed,
          completedAt: !todo.completed ? new Date().toISOString() : null
        };
      }
      return todo;
    });
    setTodoEntries(updatedEntries);
    await saveToFirestore('todoEntries', updatedEntries);
  };

  const savePacklist = async (packlist) => {
    // Sanitize packlist name and items
    const sanitizedPacklist = {
      ...packlist,
      name: validateAndSanitizeText(packlist.name, 200),
      items: sanitizeArray(packlist.items, 500)
    };

    const updatedPacklists = [...packlists, sanitizedPacklist];
    setPacklists(updatedPacklists);
    await saveToFirestore('packlists', updatedPacklists);
  };

  const deletePacklist = async (packlistId) => {
    const updatedPacklists = packlists.filter(packlist => packlist.id !== packlistId);
    setPacklists(updatedPacklists);
    await saveToFirestore('packlists', updatedPacklists);
  };

  const updatePacklist = async (updatedPacklist) => {
    // Sanitize packlist name and items
    const sanitizedPacklist = {
      ...updatedPacklist,
      name: validateAndSanitizeText(updatedPacklist.name, 200),
      items: sanitizeArray(updatedPacklist.items, 500)
    };

    const updatedPacklists = packlists.map(packlist =>
      packlist.id === sanitizedPacklist.id ? sanitizedPacklist : packlist
    );
    setPacklists(updatedPacklists);
    await saveToFirestore('packlists', updatedPacklists);
  };

  const saveDecisionEntry = async (entry) => {
    // Sanitize all text fields in the decision entry
    const sanitizedEntry = sanitizeObject(entry, [
      'title',
      'context',
      'decision',
      'reasoning',
      'alternatives',
      'consequences'
    ], 5000);

    const updatedEntries = [...decisionEntries, sanitizedEntry];
    setDecisionEntries(updatedEntries);
    await saveToFirestore('decisionEntries', updatedEntries);
  };

  const saveImage = async (imageData) => {
    const imageId = Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9);
    let imageUrl = null;
    let imageSize = imageData.file.size;

    if (!user) {
      throw new Error('User must be logged in to save images');
    }

    try {
      // Upload to Firebase Storage
      const environment = getEnvironment();
      const imageRef = ref(storage, `agriplanner/${environment}/images/${user.uid}/${imageId}`);
      const snapshot = await uploadBytes(imageRef, imageData.file);
      imageUrl = await getDownloadURL(snapshot.ref);

      const newImage = {
        id: imageId,
        name: validateAndSanitizeText(imageData.name, 200),
        description: validateAndSanitizeText(imageData.description || '', 1000),
        url: imageUrl,
        timestamp: imageData.timestamp,
        size: imageSize,
        userId: user.uid
      };

      const updatedImages = [...images, newImage];
      setImages(updatedImages);

      // Save metadata to Firestore
      const imagesMetadata = updatedImages.map(img => ({
        id: img.id,
        name: img.name,
        description: img.description,
        timestamp: img.timestamp,
        size: img.size,
        userId: img.userId
      }));

      await saveToFirestore('images', imagesMetadata);

      return newImage;
    } catch (error) {
      console.error('Error saving image:', error);
      throw error;
    }
  };

  const deleteImage = async (imageId) => {
    const imageToDelete = images.find(img => img.id === imageId);
    if (!imageToDelete) {
      console.error('Image not found:', imageId);
      return;
    }

    if (!user) {
      throw new Error('User must be logged in to delete images');
    }

    try {
      // Delete from Firebase Storage
      if (imageToDelete.url && !imageToDelete.url.startsWith('data:')) {
        try {
          const environment = getEnvironment();
          const imageRef = ref(storage, `agriplanner/${environment}/images/${user.uid}/${imageId}`);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting image from storage:', error);
          // Continue with metadata deletion even if storage deletion fails
        }
      }

      const updatedImages = images.filter(img => img.id !== imageId);
      setImages(updatedImages);

      // Update Firestore
      const imagesMetadata = updatedImages.map(img => ({
        id: img.id,
        name: img.name,
        description: img.description,
        timestamp: img.timestamp,
        size: img.size,
        userId: img.userId
      }));

      await saveToFirestore('images', imagesMetadata);
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  };

  const saveCropAreas = async (areas) => {
    setCropAreas(areas);
    await saveToFirestore('cropAreas', areas);
  };

  const saveCropAreaWidth = async (width) => {
    setCropAreaWidth(width);
    await saveToFirestore('cropAreaWidth', width);
  };

  const saveSeparationWidth = async (width) => {
    setSeparationWidth(width);
    await saveToFirestore('separationWidth', width);
  };

  const saveSeparationWidthLongitudinal = async (width) => {
    setSeparationWidthLongitudinal(width);
    await saveToFirestore('separationWidthLongitudinal', width);
  };

  const saveGpsPoints = async (points) => {
    setGpsPoints(points);
    await saveToFirestore('gpsPoints', points);
  };

  const savePlantGroups = async (groups) => {
    setPlantGroups(groups);
    await saveToFirestore('plantGroups', groups);
  };

  const loadImages = async (userId) => {
    if (!userId) {
      setImages([]);
      return null;
    }

    try {
      setLoadStatus(LOAD_STATUS.LOADING);

      const docRef = doc(db, COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists() && docSnap.data().images) {
        const firestoreImagesMetadata = docSnap.data().images;

        // For logged-in users, we need to get the download URLs for each image
        const environment = getEnvironment();
        const imagesWithUrls = await Promise.all(
          firestoreImagesMetadata.map(async (imageMetadata) => {
            try {
              const imageRef = ref(storage, `agriplanner/${environment}/images/${userId}/${imageMetadata.id}`);
              const url = await getDownloadURL(imageRef);
              return {
                ...imageMetadata,
                url
              };
            } catch (error) {
              console.error(`Error loading image ${imageMetadata.id}:`, error);
              // Return metadata without URL if image file is missing
              return {
                ...imageMetadata,
                url: null
              };
            }
          })
        );

        setImages(imagesWithUrls);
        setLoadStatus(LOAD_STATUS.IDLE);
        setLoadError(null);
        return imagesWithUrls;
      }

      setImages([]);
      setLoadStatus(LOAD_STATUS.IDLE);
      setLoadError(null);
    } catch (error) {
      console.error('Error loading images from Firestore:', error);
      setImages([]);
      setLoadStatus(LOAD_STATUS.ERROR);
      setLoadError('Failed to load images. Using default values.');
    }

    return null;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        // Load all data from Firestore
        await loadFromFirestore('mapState', setMapState, { center: { lat: 59.3293, lng: 18.0686 }, zoom: 10, mapTypeId: 'roadmap' }, user);
        await loadFromFirestore('borderData', setBorderData, [], user);
        await loadFromFirestore('gamefenceData', setGamefenceData, [], user);
        await loadFromFirestore('poleDistance', setPoleDistance, 3, user);
        await loadFromFirestore('gamefenceClosed', setGamefenceClosed, false, user);
        await loadFromFirestore('journalEntries', setJournalEntries, [], user);
        await loadFromFirestore('todoEntries', setTodoEntries, [], user);
        await loadFromFirestore('packlists', setPacklists, [], user);
        await loadFromFirestore('decisionEntries', setDecisionEntries, [], user);
        await loadImages(user.uid);
        await loadFromFirestore('cropAreas', setCropAreas, [], user);
        await loadFromFirestore('cropAreaWidth', setCropAreaWidth, 10, user);
        await loadFromFirestore('separationWidth', setSeparationWidth, 0.5, user);
        await loadFromFirestore('separationWidthLongitudinal', setSeparationWidthLongitudinal, 0.5, user);
        await loadFromFirestore('gpsPoints', setGpsPoints, [], user);
        await loadFromFirestore('plantGroups', setPlantGroups, [], user);
      } else {
        // Set defaults when not logged in
        setMapState({ center: { lat: 59.3293, lng: 18.0686 }, zoom: 10, mapTypeId: 'roadmap' });
        setBorderData([]);
        setGamefenceData([]);
        setPoleDistance(3);
        setGamefenceClosed(false);
        setJournalEntries([]);
        setTodoEntries([]);
        setPacklists([]);
        setDecisionEntries([]);
        setImages([]);
        setCropAreas([]);
        setCropAreaWidth(10);
        setSeparationWidth(0.5);
        setSeparationWidthLongitudinal(0.5);
        setGpsPoints([]);
        setPlantGroups([]);
      }
      setLoading(false);
    });

    // Capture current refs for cleanup
    const currentRetryTimeouts = retryTimeouts.current;
    const currentRetryStartTimes = retryStartTimes.current;

    return () => {
      unsubscribe();
      // Clear all retry timeouts
      currentRetryTimeouts.forEach(timeout => clearTimeout(timeout));
      currentRetryTimeouts.clear();
      currentRetryStartTimes.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    user,
    loading,
    saveStatus,
    loadStatus,
    saveError,
    loadError,
    mapState,
    borderData,
    gamefenceData,
    poleDistance,
    gamefenceClosed,
    journalEntries,
    todoEntries,
    packlists,
    decisionEntries,
    images,
    cropAreas,
    cropAreaWidth,
    separationWidth,
    separationWidthLongitudinal,
    gpsPoints,
    plantGroups,
    loginWithEmail,
    signUpWithEmail,
    logout,
    sendLoginLink,
    completeSignInWithLink,
    emailLinkSignInRequired,
    saveMapState,
    saveBorderData,
    saveGamefenceData,
    savePoleDistance,
    saveGamefenceClosed,
    saveJournalEntry,
    saveTodoEntry,
    markTodoCompleted,
    savePacklist,
    updatePacklist,
    deletePacklist,
    saveDecisionEntry,
    saveImage,
    deleteImage,
    saveCropAreas,
    saveCropAreaWidth,
    saveSeparationWidth,
    saveSeparationWidthLongitudinal,
    saveGpsPoints,
    savePlantGroups
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
